import {PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js'
import {StripeError, StripePaymentElementChangeEvent} from '@stripe/stripe-js'
import {ErrorMessage} from 'components/common/ErrorMessage'
import {FixedFooter} from 'components/common/FixedFooter'
import {Button} from 'components/system/Button'
import {usePreviousStepperPath} from 'hooks/usePreviousStepperPath'
import {useConfirmingPath, useCurrency, usePaymentPath, useSiteUrl} from 'providers/EnvironmentProvider'
import {useCrossSelling, useOrderDetailsMutators, usePaymentType} from 'providers/OrderDetailsProvider'
import {OrderProvider} from 'providers/OrderProvider'
import {FormEvent, useCallback, useEffect, useState} from 'react'
import {Composed} from 'types'
import addTrackingBreadcrumb, {BreadcrumbLevel, BreadcrumbType} from 'utils/addTrackingBreadcrumb'
import {formatMoney} from 'utils/formatMoney'
import {ga4} from 'utils/ga4'
import {getGa4OrderItems} from 'utils/getGa4OrderItems'

type Props = {
  onReady: () => void
  stripeIntent: Composed.VentrataStripeIntent
}

export const StripeForm = ({onReady, stripeIntent}: Props) => {
  const stripe = useStripe()
  const elements = useElements()
  const siteUrl = useSiteUrl()
  const currency = useCurrency()
  const crossSelling = useCrossSelling()
  const confirmingPath = useConfirmingPath()
  const paymentPath = usePaymentPath()
  const previousPath = usePreviousStepperPath(paymentPath)
  const paymentType = usePaymentType()
  const order = OrderProvider.useOrder()
  const {setPaymentType} = useOrderDetailsMutators()
  const [complete, setComplete] = useState(false)

  const onChange = useCallback(
    (event: StripePaymentElementChangeEvent) => {
      setPaymentType(event.value.type)
      setComplete(event.complete)
    },
    [setPaymentType]
  )

  useEffect(() => {
    if (!paymentType || paymentType === 'link') {
      return
    }

    ga4({
      event: 'add_payment_info',
      ecommerce: {
        payment_type: paymentType,
        checkout_type: 'custom_checkout',
        items: getGa4OrderItems(order, currency, crossSelling),
      },
    })

    addTrackingBreadcrumb({
      type: BreadcrumbType.User,
      level: BreadcrumbLevel.Info,
      message: 'Loaded Stripe payment form',
      data: {
        payment_type: paymentType,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType])

  const [error, setError] = useState<StripeError | null>(null)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    const confirmMethod = stripeIntent.type === 'paymentIntent' ? stripe.confirmPayment : stripe.confirmSetup

    const result = await confirmMethod({
      elements,
      confirmParams: {
        return_url: `${siteUrl}${confirmingPath}`,
      },
    })

    if (result.error) {
      setError(result.error)
    }
  }

  const back = useCallback(() => {
    window.location.href = previousPath
  }, [previousPath])

  const confirmButtonText =
    stripeIntent.type === 'paymentIntent' ? `Pay ${formatMoney(stripeIntent.amount, stripeIntent.currency)}` : 'Confirm'

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit} id="payment">
      <PaymentElement
        onReady={onReady}
        onChange={onChange}
        options={{
          layout: {
            type: 'accordion',
            radios: false,
            spacedAccordionItems: true,
          },
        }}
      />

      {error && <ErrorMessage error={error} />}

      <FixedFooter>
        <div className="flex gap-2">
          <Button.Back onClick={back} className="block d:hidden" />
          <Button.Primary.Orange type="submit" expanded disabled={!complete} form="payment">
            {confirmButtonText}
          </Button.Primary.Orange>
        </div>
      </FixedFooter>
    </form>
  )
}
